// navigation-completion Stage F — pure geometry helpers for the PDF.js canvas
// preview. Kept dependency-free (no pdfjs import) so the math is unit-testable
// in jsdom without a worker or a canvas.
//
// A PDF link rectangle is stored in PDF user space (origin bottom-left, y up).
// pdfjs' `viewport.convertToViewportRectangle([x0,y0,x1,y1])` maps it into the
// viewport's pixel space (origin top-left, y down) but does NOT guarantee the
// result is ordered (min,min,max,max) — the y pair comes back flipped. These
// helpers normalise that output into a CSS box an absolutely-positioned overlay
// can use, clamped to the rendered canvas so a rect that extends past the page
// edge never paints outside it.

export interface CssBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** A PDF user-space rectangle (origin bottom-left). */
export interface PdfRect {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

/**
 * Turn the 4-number array `viewport.convertToViewportRectangle` returns into a
 * normalised, clamped CSS box. `viewportWidth`/`viewportHeight` are the rendered
 * canvas CSS pixel dimensions (viewport.width/height at the render scale). A
 * degenerate or non-finite input returns null — the caller then paints no
 * highlight rather than a zero/NaN box.
 */
export function viewportRectToCssBox(
  rect: readonly number[],
  viewportWidth: number,
  viewportHeight: number,
): CssBox | null {
  if (rect.length < 4) return null;
  const [ax, ay, bx, by] = rect as [number, number, number, number];
  if (![ax, ay, bx, by, viewportWidth, viewportHeight].every((n) => Number.isFinite(n))) {
    return null;
  }
  const minX = Math.min(ax, bx);
  const maxX = Math.max(ax, bx);
  const minY = Math.min(ay, by);
  const maxY = Math.max(ay, by);
  // Clamp to the rendered canvas so the overlay never spills past the page.
  const left = clamp(minX, 0, viewportWidth);
  const top = clamp(minY, 0, viewportHeight);
  const right = clamp(maxX, 0, viewportWidth);
  const bottom = clamp(maxY, 0, viewportHeight);
  const width = right - left;
  const height = bottom - top;
  if (width <= 0 || height <= 0) return null;
  return { left, top, width, height };
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * The fit-width scale for a page whose un-scaled width is `pageWidthPts` (the
 * viewport width at scale 1) inside a container of `containerWidthPx`, bounded
 * to a sensible [min,max] so a very narrow or very wide page stays legible.
 */
export function fitWidthScale(
  pageWidthAtScale1: number,
  containerWidthPx: number,
  min = 0.25,
  max = 4,
): number {
  if (!Number.isFinite(pageWidthAtScale1) || pageWidthAtScale1 <= 0) return 1;
  if (!Number.isFinite(containerWidthPx) || containerWidthPx <= 0) return 1;
  return clamp(containerWidthPx / pageWidthAtScale1, min, max);
}

/** Clamp a requested 1-based page to [1, pageCount]; a non-finite request → 1. */
export function clampPage(requested: number, pageCount: number): number {
  if (!Number.isFinite(requested)) return 1;
  if (!Number.isFinite(pageCount) || pageCount < 1) return 1;
  return Math.max(1, Math.min(Math.trunc(requested), Math.trunc(pageCount)));
}

// ---------------------------------------------------------------------------
// navigation-visual-link-selection — pointer/text selection → PDF geometry.
//
// Selecting a source box or a destination point on the rendered canvas produces
// coordinates in the canvas' CSS pixel space (origin top-left). These helpers
// turn that into a PDF USER-SPACE rectangle/point (origin bottom-left) by
// DELEGATING the actual affine transform to pdf.js' own `convertToPdfPoint`
// inverse — so page rotation (0/90/180/270), a nonzero CropBox offset, userUnit
// and the render zoom are all handled by the live viewport, never a
// re-implemented y-flip. The only maths here is CSS→viewport pixel scaling
// (backing-store/DPR independent) and corner normalisation.
// ---------------------------------------------------------------------------

/** A point in PDF user space (origin bottom-left). */
export interface PdfPoint {
  readonly x: number;
  readonly y: number;
}

/** The minimal pdf.js PageViewport surface these helpers use. `width`/`height`
 *  are the viewport (CSS) pixel dimensions at the render scale; `convertToPdfPoint`
 *  maps a viewport-pixel point back to PDF user space. */
export interface ViewportLike {
  readonly width: number;
  readonly height: number;
  /** The page box in PDF user space `[x0,y0,x1,y1]` the viewport was built from. */
  readonly viewBox?: readonly number[];
  convertToPdfPoint(x: number, y: number): number[];
}

/**
 * Map a pointer at CSS coords (relative to the canvas CSS box top-left) to a PDF
 * user-space point. `cssWidth`/`cssHeight` are the canvas' rendered CSS size;
 * the point is first scaled into viewport pixels (so a canvas whose CSS size
 * differs from its backing store — a DPR>1 device, a zoom — is handled), then
 * run through the viewport's own inverse transform. Returns null for non-finite
 * or degenerate inputs rather than a NaN point.
 */
export function cssPointToPdfPoint(
  cssX: number,
  cssY: number,
  cssWidth: number,
  cssHeight: number,
  viewport: ViewportLike,
): PdfPoint | null {
  if (![cssX, cssY, cssWidth, cssHeight, viewport.width, viewport.height].every((n) => Number.isFinite(n))) {
    return null;
  }
  if (cssWidth <= 0 || cssHeight <= 0 || viewport.width <= 0 || viewport.height <= 0) return null;
  const vx = (cssX / cssWidth) * viewport.width;
  const vy = (cssY / cssHeight) * viewport.height;
  const pt = viewport.convertToPdfPoint(vx, vy);
  if (!Array.isArray(pt) || pt.length < 2) return null;
  const [x, y] = pt as [number, number];
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

/** Normalise two PDF-space corner points (dragged in any direction, including a
 *  reverse/bottom-up drag) into an ordered rectangle (x0<x1, y0<y1). */
export function pdfRectFromPoints(a: PdfPoint, b: PdfPoint): PdfRect | null {
  if (![a.x, a.y, b.x, b.y].every((n) => Number.isFinite(n))) return null;
  return {
    x0: Math.min(a.x, b.x),
    y0: Math.min(a.y, b.y),
    x1: Math.max(a.x, b.x),
    y1: Math.max(a.y, b.y),
  };
}

/** The page box `{x0,y0,x1,y1}` a viewport renders, from its `viewBox` (origin
 *  may be nonzero for a cropped page). Null when the viewport exposes none. */
export function pageBoxOf(viewport: ViewportLike): PdfRect | null {
  const vb = viewport.viewBox;
  if (!vb || vb.length < 4) return null;
  const [a, b, c, d] = vb as [number, number, number, number];
  if (![a, b, c, d].every((n) => Number.isFinite(n))) return null;
  return { x0: Math.min(a, c), y0: Math.min(b, d), x1: Math.max(a, c), y1: Math.max(b, d) };
}

/** Clamp a PDF-space rect to a page box so a selection dragged off the page edge
 *  never leaves the page — matching the server's on-page requirement. Returns
 *  null when the clamp collapses the rect to zero area. */
export function clampRectToBox(rect: PdfRect, box: PdfRect): PdfRect | null {
  const x0 = Math.max(Math.min(rect.x0, box.x1), box.x0);
  const x1 = Math.min(Math.max(rect.x1, box.x0), box.x1);
  const y0 = Math.max(Math.min(rect.y0, box.y1), box.y0);
  const y1 = Math.min(Math.max(rect.y1, box.y0), box.y1);
  if (x1 - x0 <= 0 || y1 - y0 <= 0) return null;
  return { x0, y0, x1, y1 };
}

/** Clamp a PDF-space point into a page box (for a destination landing point). */
export function clampPointToBox(pt: PdfPoint, box: PdfRect): PdfPoint {
  return {
    x: Math.max(box.x0, Math.min(box.x1, pt.x)),
    y: Math.max(box.y0, Math.min(box.y1, pt.y)),
  };
}

/** A gesture is selectable only when both sides clear a minimum size in PDF
 *  points — a stray click or a hairline drag is rejected, never saved as a
 *  zero/degenerate click region. */
export function isSelectableRect(rect: PdfRect | null, minPts = 2): boolean {
  if (!rect) return false;
  if (![rect.x0, rect.y0, rect.x1, rect.y1].every((n) => Number.isFinite(n))) return false;
  return rect.x1 - rect.x0 >= minPts && rect.y1 - rect.y0 >= minPts;
}
